import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { get } from 'lodash';
import { useQueryClient } from 'react-query';
import {
  Invoice, InvoiceType, SubscriptionStatus, InvoiceStatus as InvoiceStatusAPI,
} from '../../types/billing';
import { TrialExpiryCard } from './components/payments/TrialExpiryCard';
import { PauseLoader } from '../../components/PauseLoader';
import { InvoiceRow, InvoiceStatus, InvoiceTable } from './components/payments/InvoiceTable';
import { BillingSummary as BillingSummaryCard } from './components/payments/BillingSummary_';
import { useEnhancedRouter } from '../../../router';
import { noop } from '../../api/_utils';
import { PaymentSuccessModal } from './components/payments/PaymentSuccessModal';
import { CancelSubscriptionConfirmationModal } from './components/payments/CancelSubscriptionConfirmationModal';
import { PlanDetailsCard } from './components/payments/PlanDetailsCard';
import { OverdueCard } from './components/payments/OverdueCard';
import { PauseTypography } from '../../components/PauseTypography';
import { useBilling } from '../../api/billing';
import { useNotifier } from '../snackbar/store';
import { PaymentProcessingModal } from './components/payments/PaymentProcessingModal';

const formatStartDateForInvoice = (date) => moment(date).format('DD MMM');
const formatEndDateForInvoice = (date) => moment(date).format('DD MMM YYYY');
const formatInvoice = (billingDetails) => (invoice): InvoiceRow => {
  const { type } = invoice;
  const currency = billingDetails?.pricing === 'INDIAN' ? '₹' : '$';
  if (type === InvoiceType.FreeTrial) {
    return {
      // @ts-ignore
      period: `${formatStartDateForInvoice(invoice.details.periodStart)} - ${formatEndDateForInvoice(invoice.details?.periodEnd)}`,
      users: '—',
      invoiceTotal: 0,
      currency,
      status: InvoiceStatus.FreeTrial,
      paymentIntent: invoice.details?.paymentIntent,
      hostedInvoiceURL: invoice?.details?.hostedInvoiceUrl,
    };
  }
  if (type === InvoiceType.TrialExpired) {
    return {
      // @ts-ignore
      period: `${formatStartDateForInvoice(invoice.details.periodStart)} - ${formatEndDateForInvoice(invoice.details?.today)}`,
      users: invoice.details?.numberOfSeats || '—',
      invoiceTotal: '—',
      currency: '',
      status: InvoiceStatus.Pending,
      paymentIntent: invoice?.details?.paymentIntent,
      hostedInvoiceURL: invoice?.details?.hostedInvoiceUrl,
    };
  }
  if (type === InvoiceType.TrialExtended) {
    return {
      // @ts-ignore
      period: `${formatStartDateForInvoice(invoice.details.periodStart)} - ${formatEndDateForInvoice(invoice.details?.periodEnd)}`,
      users: invoice.details?.numberOfSeats || '—',
      invoiceTotal: 0,
      currency,
      status: InvoiceStatus.TrialExtended,
      paymentIntent: invoice?.details?.paymentIntent,
      hostedInvoiceURL: invoice?.details?.hostedInvoiceUrl,
    };
  }
  return {
    // @ts-ignore
    period: `${formatStartDateForInvoice(invoice.details.periodStart)} - ${formatEndDateForInvoice(invoice.details?.periodEnd)}`,
    users: invoice.details?.numberOfSeats || 0,
    invoiceTotal: (invoice.details?.totalAmount || 0) / 100,
    currency,
    status: invoice.details?.status === InvoiceStatusAPI.Open
      ? InvoiceStatus.Pending : InvoiceStatus.Paid,
    invoiceLink: invoice.details?.downloadURL || '',
    paymentIntent: invoice?.details?.paymentIntent,
    hostedInvoiceURL: invoice?.details?.hostedInvoiceUrl,
  };
};

const formatInvoices = (billingDetails) => (invoices: Invoice[]) => invoices
  .map((invoice: Invoice): InvoiceRow => formatInvoice(billingDetails)(invoice));

type BillingSummaryProps = {
  loading: boolean,
  planDetails: any,
  billingDetails: any,
  onStartSubscription: (a: { paymentMethodOnly: boolean }) => any,
  onViewPlanDetails: () => any,
  trialExpiryDate: string,
  onOpenCustomerPortal: () => any,
}
const BillingSummary = ({
  loading,
  trialExpiryDate = '',
  planDetails,
  billingDetails,
  onStartSubscription: _onStartSubscription,
  onViewPlanDetails = () => null,
  onOpenCustomerPortal = () => null,
}: BillingSummaryProps) => {
  const onStartSubscription = async () => {
    try {
      await _onStartSubscription({
        paymentMethodOnly: planDetails?.status === SubscriptionStatus.Trial,
      });
      return true;
    } catch (_e) {
      return noop();
    }
  };

  enum BillingSummaryState {
    ShowTrialExpriyCard = 'TrialExpiryCard',
    ShowBillingDetails = 'DetailsSummary',
    ShowOverdue = 'OverdueCard',
    ShowError = 'Error',
    None = 'None'
  }

  const billingSummaryState = useMemo(() => {
    if (billingDetails) {
      switch (planDetails?.status) {
        case SubscriptionStatus.Overdue:
          return BillingSummaryState.ShowOverdue;
        default:
          return BillingSummaryState.ShowBillingDetails;
      }
    } else {
      switch (planDetails?.status) {
        case SubscriptionStatus.Overdue:
          return BillingSummaryState.ShowOverdue;
        case SubscriptionStatus.Active:
          return BillingSummaryState.ShowError;
        default:
          return BillingSummaryState.ShowTrialExpriyCard;
      }
    }
  }, [planDetails, billingDetails]);

  return (loading) ? <PauseLoader />
    : (
      <>
        <Grid container spacing={4}>
          {[BillingSummaryState.ShowBillingDetails].includes(billingSummaryState) && (
            <>
              <Grid item xs={12}>
                <BillingSummaryCard
                  onEditDetails={onOpenCustomerPortal}
                />
              </Grid>
            </>
          )}

          {[BillingSummaryState.ShowTrialExpriyCard]
            .includes(billingSummaryState) ? (
            <Grid item xs={12}>
              <TrialExpiryCard
                subscriptionStatus={planDetails?.status || ''}
                trialExpiryDate={trialExpiryDate}
                onShowPlanDetails={onViewPlanDetails}
                onStartSubscription={onStartSubscription}
              />
            </Grid>
          ) : null}
          {[BillingSummaryState.ShowOverdue].includes(billingSummaryState)
            ? (
              <Grid item xs={12}>
                <OverdueCard onOpenCustomerPortal={onOpenCustomerPortal} />
              </Grid>
            ) : null}
          {[BillingSummaryState.ShowError].includes(billingSummaryState)
            ? (
              <Grid item xs={12}>
                <PauseTypography>Could not load billing details at this time.</PauseTypography>
              </Grid>
            ) : null}
        </Grid>
      </>
    );
};

const Invoices = ({
  invoices = [],
  loading = false,
}: {
  invoices: InvoiceRow[],
  loading: boolean
}) => (loading ? <PauseLoader /> : <InvoiceTable invoices={invoices} />);

export const Billing = () => {
  const {
    loadingBillingDetails,
    loadingInvoices,
    planDetails,
    invoices: invoicesFromAPI,
    billingDetails,
    startSubscription,
    cancelSubscription,
    openCustomerPortal,
    refetchSubscription,
    confirmPayment,
  } = useBilling();

  // eslint-disable-next-line no-shadow
  enum InterstitialType {
    PaymentProcessing,
    PaymentSuccess,
    ConfirmCancellation,
    PlanDetails,
    Nothing
  }

  const [interstitialType, setInterstitialType] = React.useState(InterstitialType.Nothing);
  const router = useEnhancedRouter();
  const { query } = router;
  const { enqueueNotification } = useNotifier();
  const queryClient = useQueryClient();

  const clearInterstitial = () => {
    setInterstitialType(InterstitialType.Nothing);
    router.replace(window.location.pathname);
  };

  React.useEffect(() => {
    if (query?.paymentMethod) {
      setTimeout(refetchSubscription, 1000);
    }
    if (query?.paymentSuccess) {
      setTimeout(() => {
        setInterstitialType(InterstitialType.PaymentProcessing);
      }, 0);
      setTimeout(async () => {
        await queryClient.refetchQueries('invoices');
        const hasPaymentPending = get((invoicesFromAPI || []).find(({ details }) => details?.status === InvoiceStatusAPI.Open), 'details.paymentIntent', null);
        if (hasPaymentPending) {
          confirmPayment({
            clientSecret: hasPaymentPending.clientSecret,
            paymentMethodId: billingDetails?.paymentMethodId || '',
          }).then((r) => {
            if (r?.error) {
              enqueueNotification(r?.error?.message);
              clearInterstitial();
            } else {
              queryClient.refetchQueries('invoices');
              setInterstitialType(InterstitialType.PaymentSuccess);
              return null;
            }
          }).catch(noop);
        } else setInterstitialType(InterstitialType.PaymentSuccess);
      }, 5000);
    }
    if (query?.confirmCancellation) {
      return setInterstitialType(
        InterstitialType.ConfirmCancellation,
      );
    }
    if (query?.planDetails) return setInterstitialType(InterstitialType.PlanDetails);
    return setInterstitialType(InterstitialType.Nothing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const onClosePaymentSuccessModal = () => {
    clearInterstitial();
    setTimeout(refetchSubscription, 1000);
  };

  const onCancelSubscription = async () => {
    try {
      await cancelSubscription();
      clearInterstitial();
    } catch (_e) {
      noop();
    }
  };

  const onOpenCustomerPortal = () => {
    openCustomerPortal().catch(noop);
  };

  const onStartSubscription = (a) => {
    startSubscription(a).catch(noop);
  };

  const invoices = formatInvoices(billingDetails)(invoicesFromAPI);

  return (
    <div css={(theme) => ({
      display: 'grid',
      gridGap: theme.spacing(4),
      gridAutoFlow: 'rows',
    })}
    >
      <div>
        <BillingSummary
          loading={loadingBillingDetails}
          trialExpiryDate={planDetails?.trialEndDate || ''}
          planDetails={planDetails}
          billingDetails={billingDetails}
          onStartSubscription={onStartSubscription}
          onViewPlanDetails={() => {
            router.replace(`${window.location.pathname}?planDetails=true`);
          }}
          onOpenCustomerPortal={onOpenCustomerPortal}
        />
      </div>
      <div>
        <Invoices
          invoices={invoices || []}
          loading={loadingInvoices}
        />
      </div>
      {interstitialType === InterstitialType.PaymentProcessing
        && <PaymentProcessingModal open />}

      {interstitialType === InterstitialType.PaymentSuccess
        && <PaymentSuccessModal open onClose={onClosePaymentSuccessModal} />}

      {interstitialType === InterstitialType.ConfirmCancellation
        && (
          <CancelSubscriptionConfirmationModal
            open
            onClose={clearInterstitial}
            onCancelSubscription={onCancelSubscription}
          />
        )}

      {interstitialType === InterstitialType.PlanDetails
        && (
          <PlanDetailsCard open onClose={clearInterstitial} />
        )}
    </div>
  );
};
